import React, { useState, useMemo } from 'react';
import { ArrowUpRight, Filter, X, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from './ui/input';
import { products, productCategories, productMaterials, productSizes } from '../mock/data';
import { useI18n } from '../context/I18nContext';

const FilterPill = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-xs font-semibold tracking-wide transition-all border ${
      active
        ? 'bg-[#0b2545] text-white border-[#0b2545]'
        : 'bg-white text-slate-700 border-slate-300 hover:border-[#f7a11a] hover:text-[#f7a11a]'
    }`}
  >
    {label}
  </button>
);

const Products = () => {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [material, setMaterial] = useState('All');
  const [size, setSize] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (q && !(p.title.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.material.toLowerCase().includes(q))) return false;
      if (category !== 'All' && p.category !== category) return false;
      if (material !== 'All' && p.material !== material) return false;
      if (size !== 'All' && p.size !== size) return false;
      return true;
    });
  }, [search, category, material, size]);

  const activeCount = [category, material, size].filter((x) => x !== 'All').length + (search ? 1 : 0);

  const clearAll = () => {
    setSearch('');
    setCategory('All');
    setMaterial('All');
    setSize('All');
  };

  return (
    <section id="products" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.3em] uppercase text-[#f7a11a] font-semibold">{t('products.eyebrow')}</span>
            <h2 className="mt-4 text-4xl lg:text-6xl font-black text-slate-900 leading-tight tracking-tight">
              {t('products.title1')}<br />
              <span className="text-slate-400">{t('products.title2')}</span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">{t('products.desc')}</p>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`lg:hidden inline-flex items-center gap-2 px-5 py-3 border font-semibold text-sm ${
              activeCount ? 'bg-[#f7a11a] border-[#f7a11a] text-slate-900' : 'bg-white border-slate-300 text-slate-700'
            }`}
          >
            <Filter className="w-4 h-4" /> Filters {activeCount > 0 && <span className="ml-1 bg-white/30 px-1.5 rounded-sm text-xs">{activeCount}</span>}
          </button>
        </div>

        {/* Filter Bar */}
        <div className={`${showFilters ? 'block' : 'hidden'} lg:block mb-8 pb-8 border-b border-slate-200`}>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 w-24">Search</span>
              <div className="flex-1 min-w-0 max-w-xl relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder='Try "valve", "HVAC", "geotextile"...'
                  className="pl-10 h-11 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-[#f7a11a]">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 w-24">Category</span>
              <div className="flex flex-wrap gap-2">
                {productCategories.map((c) => (
                  <FilterPill key={c} label={c} active={category === c} onClick={() => setCategory(c)} />
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 w-24">Material</span>
              <div className="flex flex-wrap gap-2">
                {productMaterials.map((c) => (
                  <FilterPill key={c} label={c} active={material === c} onClick={() => setMaterial(c)} />
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 w-24">Size</span>
              <div className="flex flex-wrap gap-2">
                {productSizes.map((c) => (
                  <FilterPill key={c} label={c} active={size === c} onClick={() => setSize(c)} />
                ))}
              </div>
            </div>
            {activeCount > 0 && (
              <div className="flex items-center gap-4 pt-2">
                <span className="text-sm text-slate-600">Showing <strong className="text-[#0b2545]">{filtered.length}</strong> of {products.length} products</span>
                <button onClick={clearAll} className="inline-flex items-center gap-1 text-xs font-semibold text-[#f7a11a] hover:text-[#e69108]">
                  <X className="w-3 h-3" /> Clear filters
                </button>
              </div>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 text-center">
            <Filter className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-600">No products match your filters.</p>
            <button onClick={clearAll} className="mt-4 text-[#f7a11a] font-semibold hover:underline">Clear filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group relative overflow-hidden bg-slate-100 aspect-[4/5] cursor-pointer">
                <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b2545] via-[#0b2545]/40 to-transparent opacity-90 group-hover:opacity-95 transition-opacity" />
                <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
                  <span className="px-2 py-1 bg-[#f7a11a] text-slate-900 text-[10px] font-bold tracking-wider">{p.category}</span>
                  <span className="px-2 py-1 bg-white/20 backdrop-blur text-white text-[10px] font-bold tracking-wider">{p.material}</span>
                </div>
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
                    <h3 className="text-white font-bold text-xl leading-tight">{p.title}</h3>
                    <p className="mt-2 text-white/80 text-sm leading-relaxed max-h-0 overflow-hidden group-hover:max-h-24 transition-all duration-500">{p.desc}</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 bg-[#f7a11a] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ArrowUpRight className="w-5 h-5 text-slate-900" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Products;
